import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { isWorkspaceIndependentKey } from '@/lib/workspace/workspaceCacheScope';
import { runWorkspaceResets } from '@/lib/workspace/workspaceResetRegistry';

const CHANNEL_NAME = 'mora-workspace-sync';

type WorkspaceBroadcastEvent =
  | { type: 'invalidate_queries'; keys: string[][] }
  | { type: 'workspace_changed'; brandKitId: string };

/**
 * Hook to synchronize query invalidations across multiple tabs.
 * Mount this in AppLayout so any open tab automatically refreshes its data
 * when another tab modifies the workspace (e.g. scheduling, approvals, creations).
 */
export function useWorkspaceBroadcastSync(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const ch = new BroadcastChannel(CHANNEL_NAME);

    ch.onmessage = (e: MessageEvent<WorkspaceBroadcastEvent>) => {
      const data = e.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'invalidate_queries' && Array.isArray(data.keys)) {
        data.keys.forEach((key) => {
          void queryClient.invalidateQueries({ queryKey: key });
        });
        return;
      }

      // A workspace switch in another tab. The pointer
      // (`user_profiles.active_brand_kit_id`) is ONE row per user, so every
      // open tab is now showing the previous workspace's data — this is not a
      // nicety, those tabs are actively wrong until they re-derive. The
      // switching tab already cleared its own caches; this does the same here.
      //
      // Deliberately NOT a full `clear()`: the switching tab preserved the
      // workspace-independent keys (credit wallet, subscription, the kit list)
      // for the same reasons, and dropping them here would make every other tab
      // flash its billing state.
      if (data.type === 'workspace_changed') {
        void queryClient.invalidateQueries({
          predicate: (query) => !isWorkspaceIndependentKey(query.queryKey),
        });
        void queryClient.invalidateQueries({ queryKey: ['active-workspace-pointer'] });
        void queryClient.invalidateQueries({ queryKey: ['effective-workspace-id'] });
        runWorkspaceResets();
      }
    };

    return () => {
      ch.close();
    };
  }, [queryClient]);
}

/**
 * Tell every other tab that this user switched workspace.
 *
 * Called by `switchWorkspace()` only. Do not call it to "refresh" — it makes
 * other tabs discard their workspace-scoped caches, which is right after a real
 * switch and wasteful otherwise.
 */
export function broadcastWorkspaceChanged(brandKitId: string): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage({ type: 'workspace_changed', brandKitId });
    ch.close();
  } catch {
    // Ignore BroadcastChannel errors (e.g., in closed contexts)
  }
}

/**
 * Broadcast query invalidation keys to all other active tabs.
 */
export function broadcastQueryInvalidation(keys: string[][]): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    ch.postMessage({
      type: 'invalidate_queries',
      keys,
    });
    ch.close();
  } catch {
    // Ignore BroadcastChannel errors (e.g., in closed contexts)
  }
}
